import fullDate from '@/filters/fullDate';
import formatTime from '@/filters/time';
import { trans } from '@/mixins/i18n';
import { decomposeWindow, windowDurationLabel } from '@/Utils/scheduleWindow';
import { DateTime } from 'luxon';

const weekdaySortOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export const sortedWeekdayLabels = (weekdays: string[] = [], locale: string = 'en'): string[] => {
    return weekdays.sort((a, b) => weekdaySortOrder.indexOf(a) - weekdaySortOrder.indexOf(b)).map((day) => localizeWeekday(day, locale, 'long'));
};

export function localizeWeekday(englishWeekday: string, locale: string = 'en', style: 'long' | 'short' | 'narrow' = 'long') {
    const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    const index = weekdays.indexOf(englishWeekday.toLowerCase());
    if (index === -1) throw new Error(`Invalid weekday: ${englishWeekday}`);

    const baseDate = new Date(Date.UTC(2025, 0, 5)); // Sunday
    baseDate.setUTCDate(baseDate.getUTCDate() + index);

    return new Intl.DateTimeFormat(locale, { weekday: style }).format(baseDate);
}

const formatOrdinalDayOfMonth = (dateTime: DateTime, locale: string): string => {
    const dayOfMonth = dateTime.day;
    if (locale && locale.toLowerCase().startsWith('en')) {
        const mod10 = dayOfMonth % 10;
        const mod100 = dayOfMonth % 100;
        let suffix = 'th';
        if (mod100 < 11 || mod100 > 13) {
            if (mod10 === 1) suffix = 'st';
            else if (mod10 === 2) suffix = 'nd';
            else if (mod10 === 3) suffix = 'rd';
        }
        return `${dayOfMonth}${suffix}`;
    }
    // Fallback for non-English locales keeps existing style
    return dateTime.toFormat('dd.');
};

export const recurrenceToHumanReadableText = (recurrence, locale: string = 'en') => {
    if (!recurrence || recurrence.repeat === 'once') {
        return trans('general.component.repeat.once');
    }

    let textBlocks = [trans('general.component.repeat.repeat_every')];

    if (recurrence.interval > 1) {
        textBlocks.push(recurrence.interval.toString());
    }

    // Repeat
    switch (recurrence.repeat) {
        case 'hourly':
            textBlocks.push(trans('general.unit.hours', { '%d': recurrence.interval || 1 }));
            break;
        case 'daily':
            textBlocks.push(trans('general.unit.days', { '%d': recurrence.interval || 1 }));
            break;
        case 'weekly':
            textBlocks.push(trans('general.unit.weeks', { '%d': recurrence.interval || 1 }));

            if (recurrence.weekdays.length > 0) {
                textBlocks.push(trans('general.component.repeat.on'));
                const sortedWeekdays = sortedWeekdayLabels(recurrence.weekdays, locale);
                textBlocks.push(sortedWeekdays.join(', '));
            }

            break;
        case 'monthly':
            textBlocks.push(trans('general.unit.months', { '%d': recurrence.interval || 1 }));
            break;
        case 'yearly':
            textBlocks.push(trans('general.unit.years', { '%d': recurrence.interval || 1 }));
            break;
    }

    // Time part (start and end)
    if (recurrence.startDate && ['daily', 'weekly', 'monthly', 'yearly'].includes(recurrence.repeat)) {
        const startTime = formatTime(recurrence.startDate, locale);
        const startDT = DateTime.fromISO(recurrence.startDate).setLocale(locale);
        const endDT = recurrence.endDate ? DateTime.fromISO(recurrence.endDate).setLocale(locale) : null;

        // Build date label for monthly/yearly and adapt to cross-day spans
        let dateLabel: string | null = null;
        let showDurationInsteadOfEndDate = false;
        if (recurrence.repeat === 'monthly') {
            dateLabel = formatOrdinalDayOfMonth(startDT, locale);
        } else if (recurrence.repeat === 'yearly') {
            dateLabel = startDT.toFormat('dd.MM.');
        }

        if (endDT && (recurrence.repeat === 'monthly' || recurrence.repeat === 'yearly')) {
            const isSameDay = startDT.toISODate() === endDT.toISODate();
            if (!isSameDay) {
                if (recurrence.repeat === 'monthly') {
                    // Within the same month use dd.-dd., if different month prefer duration to avoid implying a fixed month
                    if (endDT.hasSame(startDT, 'month')) {
                        const startDay = formatOrdinalDayOfMonth(startDT, locale);
                        const endDay = formatOrdinalDayOfMonth(endDT, locale);
                        dateLabel = `${startDay}-${endDay}`;
                    } else {
                        dateLabel = formatOrdinalDayOfMonth(startDT, locale);
                        showDurationInsteadOfEndDate = true;
                    }
                } else {
                    // Yearly: keep only the start date in the label; end date will be shown before end time
                    dateLabel = startDT.toFormat('dd.MM.');
                }
            }
        }

        if (dateLabel) {
            textBlocks.push(trans('general.component.repeat.on'));
            textBlocks.push(dateLabel);
        }

        if (!endDT) {
            textBlocks.push(trans('general.time.at'));
            textBlocks.push(startTime);
        } else {
            const endTime = formatTime(recurrence.endDate, locale);
            const isSameDay = startDT.toISODate() === endDT.toISODate();

            if (isSameDay) {
                textBlocks.push(startTime);
                textBlocks.push('-');
                textBlocks.push(endTime);
            } else {
                // Monthly across months: show duration instead of end date to avoid implying a fixed month
                if (recurrence.repeat === 'monthly' && showDurationInsteadOfEndDate) {
                    const window = decomposeWindow(startDT, endDT);
                    const durationLabel = window ? windowDurationLabel(window) : '';
                    textBlocks.push(trans('general.time.at'));
                    textBlocks.push(startTime);
                    textBlocks.push(`<br/>${trans('maintainers.sla.time_to_resolve.title')}: ${durationLabel}`);
                } else if (recurrence.repeat === 'yearly') {
                    // Show end date before end time for yearly cross-day spans
                    const endDateLabel = endDT.toFormat('dd.MM.');
                    textBlocks.push(startTime);
                    textBlocks.push('-');
                    textBlocks.push(endDateLabel);
                    textBlocks.push(endTime);
                } else if (!dateLabel) {
                    // For daily/weekly (no dateLabel), keep behavior showing end date with month
                    const endDateLabel = endDT.toFormat('dd.MM.');
                    textBlocks.push(startTime);
                    textBlocks.push('-');
                    textBlocks.push(endDateLabel);
                    textBlocks.push(endTime);
                } else {
                    // Date range already present for monthly/yearly via dateLabel
                    textBlocks.push(startTime);
                    textBlocks.push('-');
                    textBlocks.push(endTime);
                }
            }
        }
    }

    // Stop condition
    if (recurrence.stopCondition === 'count' && recurrence.count) {
        textBlocks.push(`${recurrence.count} ${trans('general.occurrences', { '%d': recurrence.count })}`);
    } else if (recurrence.stopCondition === 'until' && recurrence.until) {
        const formattedDate = fullDate(recurrence.until, locale);
        textBlocks.push(`${trans('general.component.repeat.ends.on_specific_date_short')} ${formattedDate}`);
    }

    return textBlocks.join(' ');
};
